// Utility functions for Ad Placement Configuration

export const isAdType = (key: string): boolean => {
	const adTypes = ['INTERSTITIAL', 'MREC', 'BANNER', 'BOTTOM_BANNER', 'REWARDED_INTERSTITIAL'];
	return adTypes.includes(key);
};

export const isGameType = (key: string): boolean => {
	const gameTypes = ['ludonx', 'snl', 'snlt', 'hc', 'tc', 'turbo', 'carrom'];
	return gameTypes.includes(key);
};

// Get ad type styling
export const getAdTypeStyle = (type: string, enabled: boolean) => {
	const styles = {
		INTERSTITIAL: { color: '#e74c3c', bg: '#fdf2f2', name: 'Interstitial' },
		MREC: { color: '#3498db', bg: '#f0f8ff', name: 'Medium Rectangle' },
		BANNER: { color: '#27ae60', bg: '#f0fff4', name: 'Banner' },
		BOTTOM_BANNER: { color: '#f39c12', bg: '#fffbf0', name: 'Bottom Banner' },
		REWARDED_INTERSTITIAL: { color: '#9b59b6', bg: '#f8f4ff', name: 'Rewarded Interstitial' }
	};
	const style = styles[type as keyof typeof styles] || { color: '#7f8c8d', bg: '#f8f9fa', name: type };

	return {
		...style,
		borderColor: enabled ? style.color : '#e0e0e0',
		shadowColor: enabled ? `${style.color}20` : 'rgba(0, 0, 0, 0.08)'
	};
};

// Helper function to get device config with fallback to global
export const getDeviceConfig = (adConfig: any, platform: string, subPlatform?: string, globalConfig?: any) => {
	// First check if ad type has its own deviceConfig
	if (adConfig.deviceConfig) {
		if (subPlatform) {
			return adConfig.deviceConfig[platform as keyof typeof adConfig.deviceConfig]?.[subPlatform];
		}
		return adConfig.deviceConfig[platform as keyof typeof adConfig.deviceConfig];
	}

	// Fallback to global deviceConfig
	if (globalConfig?.deviceConfig) {
		if (subPlatform) {
			return (globalConfig.deviceConfig as any)[platform]?.[subPlatform];
		}
		return (globalConfig.deviceConfig as any)[platform];
	}

	return null;
};

// Helper function to create local deviceConfig with global fallback preservation
export const createLocalDeviceConfig = (
	adConfig: any,
	platform: string,
	subPlatform: string | undefined,
	updates: any,
	globalConfig: any
) => {
	const globalDeviceConfig = (globalConfig?.deviceConfig as any) || {};

	// Start with a complete copy of global config to preserve all existing behavior
	const newDeviceConfig: any = {
		// Copy all global config values first
		android: globalDeviceConfig.android ? { ...globalDeviceConfig.android } : undefined,
		ios: globalDeviceConfig.ios ? { ...globalDeviceConfig.ios } : undefined,
		'com.zupee.free': globalDeviceConfig['com.zupee.free']
			? {
					android: globalDeviceConfig['com.zupee.free'].android
						? { ...globalDeviceConfig['com.zupee.free'].android }
						: undefined,
					ios: globalDeviceConfig['com.zupee.free'].ios ? { ...globalDeviceConfig['com.zupee.free'].ios } : undefined
			  }
			: undefined,
		// Override with any existing local config
		...adConfig.deviceConfig
	};

	// Apply the specific change
	if (subPlatform) {
		newDeviceConfig[platform] = {
			...globalDeviceConfig[platform],
			...adConfig.deviceConfig?.[platform],
			[subPlatform]: {
				...getDeviceConfig(adConfig, platform, subPlatform, globalConfig),
				...updates
			}
		};
	} else {
		newDeviceConfig[platform] = {
			...getDeviceConfig(adConfig, platform, undefined, globalConfig),
			...updates
		};
	}

	// Clean up undefined values
	Object.keys(newDeviceConfig).forEach((key) => {
		if (newDeviceConfig[key] === undefined) {
			delete newDeviceConfig[key];
		}
	});

	return newDeviceConfig;
};

// Helper function to get nested value from object
export const getNestedValue = (obj: any, path: string[]) => {
	return path.reduce((current, key) => current?.[key], obj);
};

// Format value for display in diff
export const formatValue = (value: any): string => {
	if (value === null || value === undefined) return 'null';
	if (Array.isArray(value)) return `[${value.join(', ')}]`;
	if (typeof value === 'object') return JSON.stringify(value, null, 2);
	return String(value);
};
